// Compact premium date/time field controls — PRESENTATION LAYER ONLY.
// Each field is a button that opens a Radix popover: a month calendar for dates,
// a 15-minute scroll list for times. They emit the exact same "YYYY-MM-DD" /
// "HH:mm" strings the old native inputs produced — the quote/booking flow is
// byte-for-byte unchanged and the server remains the sole authority.
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Clock3 } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { TIME_OPTIONS, fmtDayShort, startOfDay, toDateStr } from "../lib/scheduler";

const fieldClass = (invalid) =>
  `w-full min-h-14 text-left rounded-xl border px-3.5 py-2 bg-[#0A0D10] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E50914] ${
    invalid ? "border-red-500/60" : "border-[#27272A] hover:border-[#3F3F46]"
  }`;

export const calendarClassNames = {
  day: "h-9 w-9 rounded-xl text-sm text-zinc-200 hover:bg-[#1F2328] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E50914]",
  day_selected: "bg-[#E50914] text-white hover:bg-[#E50914] focus:bg-[#E50914] rounded-xl",
  day_today: "text-[#FF3B45] font-extrabold",
  day_disabled: "text-zinc-700 opacity-40 hover:bg-transparent",
  day_outside: "text-zinc-700 opacity-40",
  head_cell: "text-zinc-600 rounded-md w-9 font-semibold text-[0.65rem] uppercase tracking-wider",
  caption_label: "text-sm font-bold text-white font-display",
  cell: "h-9 w-9 p-0 text-center",
  nav_button: "h-8 w-8 bg-transparent p-0 text-zinc-400 hover:text-white border border-[#27272A] rounded-lg flex items-center justify-center",
  table: "w-full border-collapse",
};

export function DateField({ label, value, onChange, minDate, rangeStart, rangeEnd, invalid, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const from = rangeStart ? new Date(`${rangeStart}T00:00:00`) : undefined;
  const to = rangeEnd ? new Date(`${rangeEnd}T00:00:00`) : undefined;
  // Visual connection between the chosen start and end dates.
  const modifiers = {};
  if (from) modifiers.range_start = from;
  if (to) modifiers.range_end = to;
  if (from && to && to > from) modifiers.range_middle = { from, to };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={`${label}${selected ? `, ${fmtDayShort(selected)}` : ""}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={fieldClass(invalid)}
        >
          <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-500">{label}</span>
          <span className={`mt-0.5 flex items-center gap-1.5 text-sm font-semibold ${selected ? "text-white" : "text-zinc-600"}`}>
            <CalendarDays className="w-3.5 h-3.5 text-[#FF3B45] shrink-0" aria-hidden="true" />
            {selected ? fmtDayShort(selected) : "Select"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto p-2 rounded-2xl border-[#27272A] bg-[#0D1114] shadow-2xl shadow-black/60"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => { if (d) { onChange(toDateStr(d)); setOpen(false); } }}
          disabled={{ before: startOfDay(minDate || new Date()) }}
          defaultMonth={selected || from || new Date()}
          modifiers={modifiers}
          modifiersStyles={{
            range_middle: { background: "rgba(229,9,20,0.14)", borderRadius: 0 },
            range_start: { background: "#E50914", color: "#fff", borderRadius: "12px" },
            range_end: { background: "#E50914", color: "#fff", borderRadius: "12px" },
          }}
          classNames={calendarClassNames}
        />
      </PopoverContent>
    </Popover>
  );
}

// Availability state colors (Part 6/25) — server-supplied slot statuses only.
// Color is NEVER the sole signal: every option carries a text label + aria text.
const AVAIL_STYLE = {
  available: { dot: "bg-emerald-400", label: "Available" },
  held: { dot: "bg-amber-400", label: "Temporarily held" },
  booked: { dot: "bg-red-500", label: "Booked" },
};

export function TimeField({ label, value, onChange, invalid, testId, availability, date }) {
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const selLabel = value ? TIME_OPTIONS.find((o) => o.hhmm === value)?.label : null;
  // Browser-local past-slot filtering for pre-listing pickers (homepage search):
  // when the server's availability grid is present IT is authoritative; otherwise,
  // if the picked date is the driver's local "today", slots at/before the current
  // slot start are past and must not be selectable. Server-side enforcement at
  // quote/hold/booking remains the final gate — this is UX-only.
  const browserPastSlot = useMemo(() => {
    if (availability) return null;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    // No date picked yet: the earliest pickable day IS today (DateField
    // minDate), so an empty date filters exactly like today. Recomputed every
    // time the picker opens so a long-open page never serves a stale "now".
    if (date && date !== todayStr) return null;
    return Math.floor((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, date, open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(value ? `[data-hhmm="${value}"]` : "[data-hhmm]");
    if (el) el.scrollIntoView({ block: "center" });
  }, [open, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={`${label}${selLabel ? `, ${selLabel}` : ""}`}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={fieldClass(invalid)}
        >
          <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-500">{label}</span>
          <span className={`mt-0.5 flex items-center gap-1.5 text-sm font-semibold ${selLabel ? "text-white" : "text-zinc-600"}`}>
            <Clock3 className="w-3.5 h-3.5 text-[#FF3B45] shrink-0" aria-hidden="true" />
            {selLabel || "Select"}
            {value && availability?.[value] && (
              <span className={`w-1.5 h-1.5 rounded-full ${AVAIL_STYLE[availability[value]].dot}`} aria-hidden="true" />
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-52 p-1.5 rounded-2xl border-[#27272A] bg-[#0D1114] shadow-2xl shadow-black/60"
      >
        {availability && (
          <div className="flex items-center gap-3 px-2.5 py-2 border-b border-[#1F2328] mb-1" data-testid={`${testId}-legend`}>
            {Object.entries(AVAIL_STYLE).map(([k, s]) => (
              <span key={k} className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500">
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden="true" />{s.label}
              </span>
            ))}
          </div>
        )}
        {(availability || browserPastSlot != null) && (
          <p className="px-2.5 py-1.5 text-[9px] text-zinc-600" data-testid={`${testId}-past-note`}>Times that have already passed are not selectable.</p>
        )}
        <div ref={listRef} role="listbox" aria-label={label} className="max-h-64 overflow-y-auto snap-y pr-0.5">
          {TIME_OPTIONS.map((o) => {
            const sel = o.hhmm === value;
            const st = availability?.[o.hhmm];
            // The server omits slots that have already started/passed — when the
            // availability grid is loaded, an absent slot IS a past slot. Without
            // a grid (homepage search), the driver's browser-local clock decides.
            // Past slots are NOT rendered as options at all — the visible list
            // starts at the first future slot (never merely greyed out).
            const isPast = availability
              ? !st
              : (browserPastSlot != null &&
                 parseInt(o.hhmm.slice(0, 2), 10) * 60 + parseInt(o.hhmm.slice(3), 10) <= browserPastSlot);
            if (isPast) return null;
            const disabled = st === "booked";
            return (
              <button
                key={o.hhmm}
                type="button"
                data-hhmm={o.hhmm}
                role="option"
                aria-selected={sel}
                aria-disabled={disabled}
                disabled={disabled}
                aria-label={st ? `${o.label} — ${AVAIL_STYLE[st].label}` : o.label}
                data-testid={`${testId}-option-${o.hhmm.replace(":", "")}`}
                onClick={() => { onChange(o.hhmm); setOpen(false); }}
                className={`w-full snap-start h-11 px-3 rounded-lg text-sm font-semibold text-left transition-colors flex items-center justify-between gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E50914] ${
                  sel ? "bg-[#E50914] text-white" : disabled ? "text-zinc-700 cursor-not-allowed" : "text-zinc-300 hover:bg-[#1F2328]"
                }`}
              >
                <span>{o.label}</span>
                {st && !sel && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${AVAIL_STYLE[st].dot}`} aria-hidden="true" />}
              </button>
            );
          })}
          {browserPastSlot != null && browserPastSlot >= 1425 && (
            <p className="px-3 py-2.5 text-xs text-zinc-500" data-testid={`${testId}-no-slots-today`}>
              No more slots today — pick a later date.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
